import * as PourOver from 'pourover';

import { allProducts, filteredCrossedProducts, isDiscontinuedCrossed } from './stateLocators';
import { ComplianceFilter } from './filters/Compliance';
import { CrossAssessmentsFilter } from './filters/CrossAssessment';
import { AllocationFilter } from './filters/Allocation';
import { IncumbentProductsFilter } from './filters/IncumbentProducts';
import { IncumbentSellersFilter } from './filters/IncumbentSellers';
import { LockFilter } from './filters/Lock';
import { SavingsFilter } from './filters/Savings';
import { SellersWithCrossesFilter } from './filters/SellersWithCrosses';
import { SearchTextFilter } from './filters/SearchText';
import { FormularyPricingFilter } from './filters/FormularyPricingFilter';

import { UnspscsFilter } from './filters/Unspscs';
import { VarianceFilter } from './filters/Variance';
import { andIn } from './filters/FilterBase';
import { DiscontinuedFilter } from './filters/Discontinued';
import { UnpricedFilter } from './filters/Unpriced';
import { UnpricedCrossesFilter } from './filters/UnpricedCrosses';
import { DiscontinuedCrossesFilter } from './filters/DiscontinueCrosses';
import { NotDiscontinuedCrossesFilter } from './filters/NotDiscontinueCrosses';
import { CrossTypeFilter } from './filters/CrossType';
import { IncumbentSelfCrossFilter } from './filters/IncumbentSelfCross';

export function filterProducts(
  state: any,
  gridRows: any[],
  searchFilters,
  allUnspscIds: number[],
  allSellerIds: number[] = [],
  allIncumbentSellerIds: number[] = [],
): any {
  let collection = new PourOver.Collection(gridRows);

  // Create all of the individual filters and put them into a list for easy processing.
  let allFilters = [
    new AllocationFilter(),
    new ComplianceFilter(),
    new CrossAssessmentsFilter(),
    new IncumbentProductsFilter(),
    new IncumbentSellersFilter(allIncumbentSellerIds),
    new LockFilter(),
    new SavingsFilter(),
    new SellersWithCrossesFilter(allSellerIds),
    new SearchTextFilter(gridRows, state),
    new FormularyPricingFilter(),
    new UnspscsFilter(collection, allUnspscIds),
    new VarianceFilter(),
    new DiscontinuedFilter(),
    new DiscontinuedCrossesFilter(),
    new UnpricedFilter(),
    new UnpricedCrossesFilter(),
    new NotDiscontinuedCrossesFilter(),
    new CrossTypeFilter(),
    new IncumbentSelfCrossFilter()
  ];

  // Attach all of the indexes to the collection.
  collection.addFilters(allFilters.map((filter) => filter.makeIndex()));

  // Now we set all of the individual filters (based upon user input) to actually perform some subset of filtering. You
  // can think of these as the rings of a Venn diagram. Each one marks a subset of the overall products. Any unselected
  // filters just produce null match sets.

  // Iterate over all of the filters in the array and create a match set for each one.
  let matchSets = new Map();

  allFilters.forEach((filter) => {
    matchSets.set(
      filter.constructor.name,
      filter.getMatchSet(collection, searchFilters),
    );
  });

  let { basic, needsSubset } = getBasicAndNeedsSubset(allFilters);

  let filterCombinations = {
    incumbentsFilter: matchSets.get(IncumbentProductsFilter.name),
    intersection: null,
    subsets: {},
  };

  // And together all of those which do not need a subset.
  filterCombinations.intersection = intersectAll(
    filterCombinations.intersection,
    matchSets,
    basic,
  );

  // Iterate through the filters which do require a subset and launch the same function for each of them. Each one
  // will specify exactly one filter to skip for that subset.
  needsSubset.forEach((filterName) => {
    filterCombinations.subsets[filterName] = intersectAll(
      filterCombinations.intersection,
      matchSets,
      needsSubset,
      filterName,
    );
  });

  // Call a function to and together all of the remaining filters into that intersection we created above. This time
  // we're not skipping any of the subset filters, everything will get intersected.
  filterCombinations.intersection = intersectAll(
    filterCombinations.intersection,
    matchSets,
    needsSubset,
  );

  // At the moment we're still actively filtering down the set of grid rows to just those that pass the filter. But really we
  // could allow all of them through and just tell Angular UI Grid to only show those where the product is marked visible.
  let filteredRows = gridRows;

  if (filterCombinations.intersection) {
    state.products.forEach((product: any) => {
      product.visible = false;
    });

    filteredRows = collection.get(filterCombinations.intersection.cids);

    // Then mark everything that made it through the filters visible.
    filteredRows.forEach((row: any) => {
      state.products[row.productIndex].visible = true;
    });
  } else {
    filterCombinations.intersection = collection.getAllItems();

    // Mark all of the products as visible.
    state.products.forEach((product: any) => (product.visible = true));
  }

  // Let each filter individually calculate its counts and then we'll aggregate them together in one object.
  let perFilterCounts = allFilters.map((filter: any) =>
    filter.getCounts(filterCombinations),
  );

  let counts = Object.assign(
    {
      maxVariance: 0,
    },
    ...perFilterCounts,
  );

  // Loop through all the items (both filtered and not) and note the status of each one with regard to locked status,
  // variance, etc. Eric makes the argument that doing this is not that painful time wise and if we're clear in the code
  // it's not so horrible. Also, there are potentially a bunch of places we could save time in this code (in particular,
  // not always recreating our PourOver indexes) so if we get into performance problems we can tap some of those.
  // Another area for improvement would be something like variance. Figure it out once and save it on the product at the
  // beginning. It's not going to change, nor will its index.
  let productsIterator = allProducts(state.products);
  let incumbentSellers = new Set();
  let incumbentsWithCrosses = new Set();

  let filteredCrossesBySeller = new Map<number, Set<number>>();
  let filteredDistinctBySeller = new Map<number, Set<number>>();
  let filteredDistinctPricedBySeller = new Map<number, Set<number>>();

  let notUnpriced = 0;
  let notUnpricedCrosses = 0;


  let productCounter = 0;
  counts.notDiscontinuedCrosses = 0;
  counts.discontinuedCrosses = 0;
  counts.notUnpricedCrosses = 0;
  counts.unpricedCrosses = 0;




  for (let value of productsIterator) {
    productCounter++;
    if (value && value.visible) {

      // Is the variance greater than the maximum variance we've seen thus far?
      if (Math.abs(value.calculations.variance) > counts.maxVariance) {
        counts.maxVariance = value.calculations.variance;
      }

      // Is it a locked product?
      if (value.product.lockedAllocation) {
        counts.locked++;
      }

      // Is is a not fully allocated product?
      if (
        !value.hasOwnProperty('incumbent') &&
        value.calculations.totalAllocation < 100
      ) {
        counts.unallocated++;
      }

      if (!value.hasOwnProperty('incumbent') && value.discontinued) {
        counts.discontinued++;
      }

      if (!value.hasOwnProperty('incumbent') && value.unpriced) {
        counts.unpriced++;
      }

      if (!value.hasOwnProperty('incumbent') && value.unpricedCrossed) {
        counts.unpricedCrosses++;
      }

      if (!value.hasOwnProperty('incumbent') && value.discontinuedCrossed) {
        counts.discontinuedCrosses++;
      }

      if (!value.hasOwnProperty('incumbent') && value.notDiscontinuedCrossed) {
        counts.notDiscontinuedCrosses++;
      }


      // If it's an incumbent product, does it have a different seller than we've seen previously (we use that to get
      // a count), and does it have crosses?
      if (!value.hasOwnProperty('incumbent')) {

        incumbentSellers.add(value.product.seller.id);
      } else {
        incumbentsWithCrosses.add(value?.incumbent.incumbentProductId);

        // We're looking at filtered crossed products, we can use this to add to counts for crosses (per offer) and
        // distinct priced (per offer).
        addToSetForOffer(
          filteredCrossesBySeller,
          value.offerId,
          value?.incumbent.incumbentProductId,
        );

        if (
          value.product.unitPrice !== null &&
          !value.product.usedIncumbentPrice
        ) {
          addToSetForOffer(
            filteredDistinctPricedBySeller,
            value.offerId,
            value.product.productId,
          );
        }

        addToSetForOffer(
          filteredDistinctBySeller,
          value.offerId,
          value.product.productId,
        );
      }
    }
    counts.incumbentSellers = incumbentSellers.size;
    counts.incumbentsWithCrosses = incumbentsWithCrosses.size;

    counts.offerCounts = combineOfferCounts(
      filteredCrossesBySeller,
      filteredDistinctPricedBySeller,
      filteredDistinctBySeller,
    );
  }

  counts.maxVariance = Math.floor(counts.maxVariance);

  // Unlocked is a calculated value.
  counts.unlocked = counts.incumbents - counts.locked;

  // not Discontinued is a calculated value.
  let notDiscontinued = counts.incumbents - counts.discontinued;
  counts.notDiscontinued = notDiscontinued < 0 ? 0 : notDiscontinued;

  // not unpriced is a calculated value.
  notUnpriced = counts.incumbents - counts.unpriced;

  counts.notUnpriced = notUnpriced < 0 ? 0 : notUnpriced;


  notUnpricedCrosses = counts.incumbents - counts.unpricedCrosses;
  counts.notUnpricedCrosses = notUnpricedCrosses < 0 ? 0 : notUnpricedCrosses;

  return {
    gridRows: filteredRows,
    counts,
  };
}

interface FiltersReduction {
  basic: [];
  needsSubset: [];
}

/**
 * Intersect all of the match sets and create any subsets which skip some portion of the match sets at the same time.
 * Divide the filters into two groups. Those which need a subset of the intersection for counting purposes and those
 * which do not. This is weird and hard to explain but I'm going to try anyway. There are a group of filters where they
 * want us to display a specific count for how many that particular value corresponds to (for example,
 * Positive Savings (24) and Negative Savings (4)). For those particular ones, we need to group everything but those
 * filters together so that we can test each value individually (first positive, then negative) to see how many we get
 * for each possible value.
 *
 * This is weird and clunky, but PourOver is fast enough to make it practical even for really large numbers of filters
 * without it getting completely unmanageable. But don't think that the filtering/counting stuff is easy, it's crazy
 * hard, but it's a lot faster to do it locally rather than turn around and feed every value for 11 different filters
 * to the back-end, have them do a SQL query and come back to us with counts.
 */
function getBasicAndNeedsSubset(allFilters): FiltersReduction {
  return allFilters.reduce(
    (accumulator, filter) => {
      if (filter.needsSubset) {
        accumulator.needsSubset.push(filter.constructor.name);
      } else {
        if (!filter.skipIntersection) {
          accumulator.basic.push(filter.constructor.name);
        }
      }

      return accumulator;
    },
    {
      basic: [],
      needsSubset: [],
    },
  );
}

/**
 * Intersects all of the specified match sets together (skipping a specific one if needed) and returns the result.
 *
 * @param existingMatchSet
 * @param matchSets
 * @param toInclude
 * @param skipMatchSet
 */
function intersectAll(
  existingMatchSet: any,
  matchSets: Map<string, any>,
  toInclude: string[],
  skipMatchSet?: string,
) {
  toInclude.forEach((filterName) => {
    if (filterName !== skipMatchSet) {
      existingMatchSet = andIn(existingMatchSet, matchSets.get(filterName));
    }
  });

  return existingMatchSet;
}

function addToSetForOffer(
  setBySeller: Map<number, Set<number>>,
  key: number,
  value: number,
) {
  let set = setBySeller.get(key) ?? new Set<number>();
  set.add(value);
  setBySeller.set(key, set);
}

function combineOfferCounts(
  filteredCrossesBySeller,
  filteredDistinctPricedBySeller,
  filteredDistinctBySeller,
) {
  let offerCounts = new Map<number, any>();

  offerCounts = addToOfferCounts(
    offerCounts,
    filteredCrossesBySeller,
    'filteredCrosses',
  );
  offerCounts = addToOfferCounts(
    offerCounts,
    filteredDistinctPricedBySeller,
    'filteredDistinctPriced',
  );
  offerCounts = addToOfferCounts(
    offerCounts,
    filteredDistinctBySeller,
    'filteredDistinct',
  );

  return Object.fromEntries(offerCounts.entries());
}

function addToOfferCounts(
  offerCounts,
  offerToSetMap: Map<number, Set<number>>,
  countName,
) {
  offerToSetMap.forEach((value, key) => {
    let counts = offerCounts.get(key) ?? {};
    counts[countName] = value.size;
    offerCounts.set(key, counts);
  });

  return offerCounts;
}

